import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { Button, Input, Label } from '@ci-ville/ui';
import { api, ApiError } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
/** Activation de la double authentification : code QR, vérification, codes de secours. */
export function TwoFactorSetup({ onStart, onDone }) {
    const { applySession } = useAuth();
    const [step, setStep] = useState('intro');
    const [qr, setQr] = useState(null);
    const [code, setCode] = useState('');
    const [codes, setCodes] = useState([]);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const start = async () => {
        setBusy(true);
        try {
            setQr(await api.auth.setup2fa());
            setStep('scan');
            onStart?.();
        }
        catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Impossible de générer le code QR');
        }
        finally {
            setBusy(false);
        }
    };
    const confirm = async () => {
        setBusy(true);
        try {
            const res = await api.auth.enable2fa(code);
            await applySession(res.accessToken);
            setCodes(res.recoveryCodes);
            setStep('codes');
        }
        catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Code invalide');
        }
        finally {
            setBusy(false);
        }
    };
    const copyCodes = async () => {
        try {
            await navigator.clipboard.writeText(codes.join('\n'));
            setCopied(true);
        }
        catch {
            toast.error('Copie impossible : notez les codes à la main');
        }
    };
    if (step === 'intro') {
        return (_jsxs("div", { className: "space-y-3", children: [_jsx("p", { className: "text-sm text-muted-foreground", children: "\u00C0 chaque connexion, en plus du mot de passe, vous saisirez un code \u00E0 6 chiffres affich\u00E9 par une application d'authentification sur votre t\u00E9l\u00E9phone : Microsoft Authenticator, Google Authenticator ou \u00E9quivalent." }), _jsxs(Button, { variant: "brand", onClick: start, disabled: busy, children: [busy ? _jsx(Loader2, { className: "animate-spin" }) : _jsx(Smartphone, { className: "size-4" }), "Configurer la double authentification"] })] }));
    }
    if (step === 'scan' && qr) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsxs("ol", { className: "list-decimal space-y-1 pl-5 text-sm text-muted-foreground", children: [_jsx("li", { children: "Ouvrez votre application d'authentification et ajoutez un compte." }), _jsx("li", { children: "Scannez ce code QR (ou saisissez la cl\u00E9 manuellement)." }), _jsx("li", { children: "Entrez le code \u00E0 6 chiffres affich\u00E9." })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-4", children: [_jsx("img", { src: qr.qrDataUrl, alt: "Code QR de double authentification", className: "size-44 rounded-md border border-border bg-white p-1" }), _jsxs("div", { className: "min-w-0 space-y-1 text-xs", children: [_jsx("p", { className: "text-muted-foreground", children: "Cl\u00E9 manuelle" }), _jsx("code", { className: "block break-all rounded bg-muted px-2 py-1 font-mono", children: qr.secret })] })] }), _jsxs("div", { className: "flex items-end gap-2", children: [_jsxs("div", { className: "space-y-1", children: [_jsx(Label, { htmlFor: "tfa-code", children: "Code \u00E0 6 chiffres" }), _jsx(Input, { id: "tfa-code", inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, value: code, onChange: (e) => setCode(e.target.value.replace(/\D/g, '')), className: "w-36 font-mono text-lg tracking-widest", autoFocus: true })] }), _jsxs(Button, { variant: "brand", onClick: confirm, disabled: code.length !== 6 || busy, children: [busy ? _jsx(Loader2, { className: "animate-spin" }) : _jsx(ShieldCheck, { className: "size-4" }), "Activer"] })] })] }));
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "rounded-md border border-border bg-muted/50 p-3 text-sm", children: [_jsx("strong", { children: "Double authentification activ\u00E9e." }), " Conservez ces 10 codes de secours en lieu s\u00FBr : chacun permet une connexion si vous perdez votre t\u00E9l\u00E9phone. Ils ne seront plus affich\u00E9s."] }), _jsx("div", { className: "grid grid-cols-2 gap-1 rounded-md bg-muted p-3 font-mono text-sm sm:grid-cols-5", children: codes.map((c) => _jsx("span", { children: c }, c)) }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { variant: "outline", onClick: copyCodes, children: [copied ? _jsx(Check, { className: "size-4" }) : _jsx(Copy, { className: "size-4" }), copied ? 'Copiés' : 'Copier les codes'] }), onDone && _jsx(Button, { variant: "brand", onClick: onDone, children: "J'ai not\u00E9 mes codes" })] })] }));
}
//# sourceMappingURL=TwoFactorSetup.js.map